import React, { useState, useRef, useEffect } from 'react';
import { 
  ZoomIn, 
  ZoomOut, 
  RotateCw, 
  Check, 
  X, 
  Sparkles, 
  Sliders, 
  Move, 
  Crop as CropIcon,
  Image as ImageIcon
} from 'lucide-react';
import { Modal } from './Modal';

interface ImageCropperModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  aspectRatio: '1:1' | '16:9';
  title?: string;
  subtitle?: string;
  targetWidth?: number;
  targetHeight?: number;
  onCropComplete: (croppedDataUrl: string, stats: { originalSizeKb: number; optimizedSizeKb: number }) => void;
}

export const ImageCropperModal: React.FC<ImageCropperModalProps> = ({
  isOpen,
  onClose,
  imageSrc,
  aspectRatio,
  title = 'Crop & Optimize Image',
  subtitle = 'Adjust positioning and crop boundaries',
  targetWidth,
  targetHeight,
  onCropComplete,
}) => {
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0); // in percent (-50 to +50)
  const [panY, setPanY] = useState(0); // in percent (-50 to +50)
  const [rotation, setRotation] = useState(0); // 0, 90, 180, 270
  const [naturalWidth, setNaturalWidth] = useState(0);
  const [naturalHeight, setNaturalHeight] = useState(0);
  const [originalSizeKb, setOriginalSizeKb] = useState(0);
  const [previewOptimizedKb, setPreviewOptimizedKb] = useState(0);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Compute target canvas output dimensions
  const outWidth = targetWidth || (aspectRatio === '1:1' ? 400 : 1200);
  const outHeight = targetHeight || (aspectRatio === '1:1' ? 400 : 675);

  // Calculate approximate file size from base64
  useEffect(() => {
    if (imageSrc) {
      const length = imageSrc.length;
      const sizeInKb = Math.round((length * 3) / 4 / 1024);
      setOriginalSizeKb(sizeInKb || 850);
      // reset transforms on new image
      setZoom(1);
      setPanX(0);
      setPanY(0);
      setRotation(0);

      const img = new Image();
      img.onload = () => {
        setNaturalWidth(img.naturalWidth);
        setNaturalHeight(img.naturalHeight);
      };
      img.src = imageSrc;
    }
  }, [imageSrc]);

  // Live Canvas Rendering & Optimization calculation
  useEffect(() => {
    if (!isOpen || !imageSrc) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      canvas.width = outWidth;
      canvas.height = outHeight;

      ctx.clearRect(0, 0, outWidth, outHeight);
      ctx.save();

      // Translate to center
      ctx.translate(outWidth / 2, outHeight / 2);
      ctx.rotate((rotation * Math.PI) / 180);

      // Determine base scale to cover target canvas
      const isRotated90 = rotation % 180 !== 0;
      const effectiveImgWidth = isRotated90 ? img.naturalHeight : img.naturalWidth;
      const effectiveImgHeight = isRotated90 ? img.naturalWidth : img.naturalHeight;

      const scaleX = outWidth / effectiveImgWidth;
      const scaleY = outHeight / effectiveImgHeight;
      const baseScale = Math.max(scaleX, scaleY);
      const finalScale = baseScale * zoom;

      // Pan translation
      const offsetX = (panX / 100) * (outWidth * 0.7);
      const offsetY = (panY / 100) * (outHeight * 0.7);

      ctx.drawImage(
        img,
        - (img.naturalWidth * finalScale) / 2 + (isRotated90 ? offsetY : offsetX),
        - (img.naturalHeight * finalScale) / 2 + (isRotated90 ? offsetX : offsetY),
        img.naturalWidth * finalScale,
        img.naturalHeight * finalScale
      );

      ctx.restore();

      // Estimate compressed size
      const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
      const optKb = Math.round((dataUrl.length * 3) / 4 / 1024);
      setPreviewOptimizedKb(optKb);
    };
    img.src = imageSrc;
  }, [isOpen, imageSrc, zoom, panX, panY, rotation, outWidth, outHeight]);

  const handleApplyCrop = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
    onCropComplete(dataUrl, {
      originalSizeKb: originalSizeKb || 850,
      optimizedSizeKb: previewOptimizedKb || 120,
    });
    onClose();
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const percentReduction = originalSizeKb > previewOptimizedKb 
    ? Math.round(((originalSizeKb - previewOptimizedKb) / originalSizeKb) * 100)
    : 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      maxWidth="lg"
    >
      <div className="space-y-6 text-xs">
        {/* Aspect Ratio Guide & Optimizer Pill */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] light:bg-[#F4F8F5] light:border-[#D5E4DC]">
          <div className="flex items-center gap-2">
            <CropIcon className="w-4 h-4 text-brand-400" />
            <span className="font-bold text-white dark:text-white light:text-[#0D1C17]">
              Target Aspect Ratio: <span className="text-brand-400">{aspectRatio}</span>
            </span>
            <span className="text-[10px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
              ({outWidth} × {outHeight} px)
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              {percentReduction > 0 ? `Optimized by -${percentReduction}%` : 'High Fidelity WebP/JPEG'}
            </span>
            <span className="text-[11px] text-[#90cbb7]">
              {originalSizeKb} KB ➔ <strong className="text-emerald-400">{previewOptimizedKb} KB</strong>
            </span>
          </div>
        </div>

        {/* Interactive Cropper Viewport */}
        <div className="relative w-full rounded-2xl bg-[#0c1a15] border border-[#22493c] overflow-hidden flex items-center justify-center p-4 min-h-[320px]">
          {/* Background Canvas (hidden or live preview) */}
          <div className={`relative overflow-hidden border-2 border-brand-500 shadow-glow-md bg-[#10231d] ${
            aspectRatio === '1:1' ? 'w-64 h-64 rounded-2xl' : 'w-full max-w-[480px] aspect-video rounded-2xl'
          }`}>
            <canvas 
              ref={canvasRef} 
              className="w-full h-full object-cover"
            />

            {/* Circular dashed overlay guide for 1:1 avatar */}
            {aspectRatio === '1:1' && (
              <div className="absolute inset-2 border-2 border-dashed border-white/50 rounded-full pointer-events-none" />
            )}

            {/* 3x3 Rule-of-thirds grid for 16:9 */}
            {aspectRatio === '16:9' && (
              <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none opacity-30">
                <div className="border-r border-b border-white" />
                <div className="border-r border-b border-white" />
                <div className="border-b border-white" />
                <div className="border-r border-b border-white" />
                <div className="border-r border-b border-white" />
                <div className="border-b border-white" />
                <div className="border-r border-white" />
                <div className="border-r border-white" />
                <div />
              </div>
            )}
          </div>
        </div>

        {/* Interactive Sliders: Zoom & Pan */}
        <div className="p-4 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] space-y-4">
          {/* Zoom */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-[11px] font-semibold text-[#90cbb7]">
              <span className="flex items-center gap-1.5">
                <ZoomIn className="w-3.5 h-3.5 text-brand-400" />
                Zoom Level:
              </span>
              <span className="font-bold text-white">{zoom.toFixed(1)}x</span>
            </div>
            <div className="flex items-center gap-3">
              <ZoomOut className="w-3.5 h-3.5 text-[#90cbb7]" />
              <input
                type="range"
                min="1"
                max="3"
                step="0.05"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="w-full accent-[#0df2a6] cursor-pointer"
              />
              <ZoomIn className="w-3.5 h-3.5 text-brand-400" />
            </div>
          </div>

          {/* Horizontal Pan */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-[11px] font-semibold text-[#90cbb7]">
                <span className="flex items-center gap-1.5">
                  <Move className="w-3.5 h-3.5 text-brand-400" />
                  Horizontal Position:
                </span>
                <span className="font-bold text-white">{panX > 0 ? `+${panX}%` : `${panX}%`}</span>
              </div>
              <input
                type="range"
                min="-50"
                max="50"
                step="1"
                value={panX}
                onChange={(e) => setPanX(parseInt(e.target.value))}
                className="w-full accent-[#0df2a6] cursor-pointer"
              />
            </div>

            {/* Vertical Pan */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-[11px] font-semibold text-[#90cbb7]">
                <span className="flex items-center gap-1.5">
                  <Move className="w-3.5 h-3.5 text-brand-400" />
                  Vertical Position:
                </span>
                <span className="font-bold text-white">{panY > 0 ? `+${panY}%` : `${panY}%`}</span>
              </div>
              <input
                type="range"
                min="-50"
                max="50"
                step="1"
                value={panY}
                onChange={(e) => setPanY(parseInt(e.target.value))}
                className="w-full accent-[#0df2a6] cursor-pointer"
              />
            </div>
          </div>

          {/* Quick Actions Strip */}
          <div className="pt-2 border-t border-[#22493c] flex items-center justify-between">
            <button
              type="button"
              onClick={handleRotate}
              className="px-3 py-1.5 rounded-xl bg-[#152a23] hover:bg-[#22493c] text-[#90cbb7] hover:text-white transition-all flex items-center gap-1.5 text-xs font-semibold"
            >
              <RotateCw className="w-3.5 h-3.5" />
              <span>Rotate 90° ({rotation}°)</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setZoom(1);
                setPanX(0);
                setPanY(0);
                setRotation(0);
              }}
              className="text-[11px] text-[#90cbb7] hover:underline"
            >
              Reset to Center
            </button>
          </div>
        </div>

        {/* Modal Buttons */}
        <div className="pt-3 border-t border-[#22493c] flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-semibold text-[#90cbb7] hover:text-white"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleApplyCrop}
            className="px-6 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-400 text-forest-void font-extrabold text-xs shadow-glow-sm transition-all flex items-center gap-2"
          >
            <Check className="w-4 h-4 stroke-[3]" />
            <span>Apply Cropped & Optimized Image</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};
